import {
  type IUser,
  getValidInviteeRoles,
  isInternalRole,
  isUserRoleGreater,
  UserRole
} from './user';

export class SamlConfigForbiddenError extends Error {
  override readonly name = 'SamlConfigForbiddenError';

  constructor(message: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export interface SamlConfig {
  idpMetadataUrl?: string;
  logoutRedirectUrl?: string;
  enabled: boolean;
  forbidPasswordLogins: boolean;
  provisioningEnabled?: boolean;
  lockRoleManagement?: boolean;
}

export function isSamlRoleManagementLocked(config: SamlConfig | null): boolean {
  return Boolean(config?.provisioningEnabled === true && config?.lockRoleManagement === true);
}

export function shouldBypassSamlRoleManagementLock(actor: IUser): boolean {
  return actor.role === UserRole.OWNER || isInternalRole(actor.role);
}

export function ensureNewConfigAllowed(
  user: IUser,
  currentConfig: SamlConfig | null,
  mergedConfig: SamlConfig
): void {
  if (!isSamlRoleManagementLocked(currentConfig)) {
    return;
  }
  if (shouldBypassSamlRoleManagementLock(user)) {
    return;
  }
  if (
    mergedConfig.enabled === false ||
    mergedConfig.provisioningEnabled === false ||
    mergedConfig.lockRoleManagement === false
  ) {
    throw new SamlConfigForbiddenError(
      'Cannot disable SSO, provisioning, or role management while role management is locked'
    );
  }
}

export function ensureConfigResetAllowed(user: IUser, currentConfig: SamlConfig | null): void {
  if (!isSamlRoleManagementLocked(currentConfig)) {
    return;
  }
  if (shouldBypassSamlRoleManagementLock(user)) {
    return;
  }
  throw new SamlConfigForbiddenError(
    'Cannot reset SSO configuration while role management is locked'
  );
}

export function ensureRoleAllowed(
  user: IUser,
  samlConfig: SamlConfig | null,
  targetRole: UserRole
): void {
  if (shouldBypassSamlRoleManagementLock(user)) {
    return;
  }
  if (!isSamlRoleManagementLocked(samlConfig)) {
    return;
  }

  const isInviteeRoleAboveOperator =
    getValidInviteeRoles().includes(targetRole) && isUserRoleGreater(targetRole, UserRole.OPERATOR);

  if (isInviteeRoleAboveOperator) {
    throw new SamlConfigForbiddenError(
      'Roles above Operator are not allowed while role management is locked for SSO'
    );
  }
}

export function isSAMLWhitelistedURL(url: string): boolean {
  const whitelistedHosts = [
    'corewellhealth.org',
    'duosecurity.com',
    'jumpcloud.com',
    'loginmt.com',
    'mainfreight.com',
    'microsoftonline.com',
    'oastify.com',
    'okta.com',
    'onelogin.com',
    'vermeer.com'
  ];

  if (process.env.NODE_ENV !== 'production') {
    whitelistedHosts.push('127.0.0.1', 'localhost');
  }

  try {
    const { hostname } = new URL(url);
    return whitelistedHosts.some(
      whitelistedHost => hostname === whitelistedHost || hostname.endsWith(`.${whitelistedHost}`)
    );
  } catch (e) {
    return false;
  }
}
